import { ButtonAnchor, H4, P1, P2, Tooltip } from '@zooplus/ui-components';
import { useTranslation } from '../../../hooks/use-translations';
import React, { useContext } from 'react';
import styles from './Subscription.module.scss';
import useMedias from '../../../hooks/useMedias';
import { getUrlLocalised } from '../../../hooks/use-translations';
import { MiniProductsList } from '../../common/mini-products-list/MiniProductsList';
import { useSiteContext } from '@zooplus/commons-lang';
import AutoshipmentSiteContext from '../../../common/contexts/AutoshipmentSiteContext';
import { InfoCircle } from '@zooplus/icons';

export type SubscriptionProps = {
  status: string;
  name: string;
  weeks: number;
  totalValue: string;
  nextOrderDate: Date;
  articles: any[];
  autoshipId: number;
};

export const Subscription = (props: SubscriptionProps) => {
  const { t } = useTranslation();
  const { isMobile } = useMedias();
  const { locale } = useSiteContext();
  const { isRelaxDelivery, preponeDays, deliveryMinDays, deliveryMaxDays } =
    useContext(AutoshipmentSiteContext);

  const url = '/account/autoshipment#/edit/';
  // the date is going to be formatted depending on the locale
  const nextOrderDateFormatted = props.nextOrderDate.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  const lastDateForEdit = new Date(
    props.nextOrderDate.getTime() - (preponeDays ? preponeDays + 1 : 2) * 24 * 60 * 60 * 1000,
  ).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
  });

  const buttonType =
    props.status === 'PAUSED' ? 'reactivate-subscription' : 'adjust-subscription-products';

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <H4 className={styles.title}>
          {props.name === '' ? t('upcoming-subscriptions.subscription.name') : props.name}
        </H4>
        <P2
          className={
            styles.details
          }>{`${t('upcoming-subscriptions.subscription.products', { count: props.articles.length })} - ${t('common.weeks', { count: props.weeks / 7 })}`}</P2>

        <H4 className={styles.title2}>{t('upcoming-subscriptions.subscription.value')}</H4>
        <P1 className={styles.value}>{props.totalValue}</P1>
      </div>
      <div className={styles.body}>
        {props.status === 'PAUSED' ? (
          <H4 className={styles.datePaused}>{t('upcoming-subscriptions.subscription.paused')}</H4>
        ) : (
          <>
            <div className={styles.date}>
              <H4 className={styles.dateValue}>
                {t(
                  isRelaxDelivery
                    ? 'upcoming-subscriptions.subscription.delivery-date'
                    : 'upcoming-subscriptions.subscription.date',
                  { date: nextOrderDateFormatted },
                )}
              </H4>
              {isRelaxDelivery && deliveryMinDays && deliveryMaxDays && (
                <Tooltip
                  position="bottom-right"
                  tipId="ausNextDeliveryDateInfo"
                  tipText={
                    <P2 bold data-zta="deliveryIntervalInfo">
                      {t('upcoming-subscriptions.subscription.delivery-interval', {
                        minDay: deliveryMinDays,
                        maxDay: `+${deliveryMaxDays}`,
                      })}
                    </P2>
                  }>
                  <InfoCircle
                    aria-label="More information."
                    className={styles.moreInfoIcon}
                    size={20}
                  />
                </Tooltip>
              )}
            </div>
            <P2 className={styles.lastEditDate}>
              {t('upcoming-subscriptions.subscription.edit-until', {
                lastDate: lastDateForEdit,
              })}
            </P2>
          </>
        )}
        <MiniProductsList products={props.articles} hasAnchor={false} />
        <div className={styles.separator} />
      </div>
      <div className={styles.buttonWrapper}>
        <ButtonAnchor
          id={`${buttonType}-${props.autoshipId}`}
          data-zta={buttonType}
          href={getUrlLocalised(t, `${url}${props.autoshipId}`)}
          length={isMobile ? 'full' : 'normal'}
          size="small"
          target="_blank"
          className={styles.button}>
          {props.status === 'PAUSED'
            ? t('upcoming-subscriptions.subscription.primary-button-pause')
            : t('upcoming-subscriptions.subscription.primary-button')}
        </ButtonAnchor>
        {props.status === 'ACTIVE' && (
          <ButtonAnchor
            id={`change-subscription-date-${props.autoshipId}`}
            data-zta={'change-subscription-date'}
            href={getUrlLocalised(t, `${url}${props.autoshipId}`)}
            length={isMobile ? 'full' : 'normal'}
            size="small"
            target="_blank"
            variant="secondary"
            className={styles.button}>
            {t('upcoming-subscriptions.subscription.secondary-button')}
          </ButtonAnchor>
        )}
      </div>
    </div>
  );
};
